import { usePlanChoice, type PlanChoiceOptions } from './usePlanChoice';
import { CheckIcon } from './icons';

export interface PlanSelectorProps extends PlanChoiceOptions {
  productId: string;
}

/**
 * Plan selection, as a list of cards. All copy comes from the server (`GET /plans`) — the client
 * has no hardcoded pricing, so a plan change is a deploy of config, not of the UI.
 *
 * The flow lives in `usePlanChoice`; everything below is markup. Tenders renders the same flow as a
 * comparison table instead (apps/tenders/src/PlanLedger.tsx) — if you are changing what CHOOSING a
 * plan does, the hook is the file, and both screens get it.
 */
export function PlanSelector({ productId, checkoutReturnUrl, onEntitled }: PlanSelectorProps) {
  const { plans, error, busyPlan, mayBuy, choose } = usePlanChoice(productId, {
    checkoutReturnUrl,
    onEntitled,
  });

  if (error && !plans) return <p role="alert">{error}</p>;
  if (!plans) return <p data-testid="plans-loading">טוען תוכניות…</p>;

  return (
    <div className="huaca-plans" data-testid="plan-selector">
      <h1>בחירת תוכנית</h1>
      {!mayBuy && (
        <p role="note" data-testid="not-owner-note">
          רק בעלים של הארגון יכולים להתחיל תוכנית. בקשו מהם לבחור תוכנית לצוות.
        </p>
      )}

      <ul>
        {plans.map((plan) => (
          <li
            key={plan.id}
            className={plan.display.badge ? 'huaca-plan-featured' : undefined}
            data-testid={`plan-${plan.id}`}
          >
            {plan.display.badge && <span data-testid="badge">{plan.display.badge}</span>}
            <h2>{plan.display.name}</h2>
            <p>{plan.display.description}</p>
            <p data-testid="price">{plan.display.priceLabel}</p>
            <ul>
              {plan.display.features.map((f) => (
                <li key={f}>
                  <CheckIcon className="huaca-icon--sm" />
                  {f}
                </li>
              ))}
            </ul>
            {mayBuy && (
              <button type="button" className={plan.display.badge ? 'btn-cta' : 'btn-primary'} disabled={busyPlan !== null} onClick={() => void choose(plan)} data-testid={`choose-${plan.id}`}>
                {busyPlan === plan.id
                  ? 'מעבד…'
                  : plan.trialDays !== null
                    ? `התחלת ניסיון חינם ל-${plan.trialDays} ימים`
                    : 'בחירת תוכנית'}
              </button>
            )}
          </li>
        ))}
      </ul>

      {error && (
        <p role="alert" data-testid="plan-error">
          {error}
        </p>
      )}
    </div>
  );
}
